import * as React from 'react'
import { GoalWithProgress, Role, UserCompanyDto } from '../../services/api.types'
import { Card } from '../Card/Card'
import { t, c } from 'ttag'
import { Box, Grid } from '@material-ui/core'
import Loader from '../Loader/Loader'
import { convertUTCToLocalDate } from '../../common/helpers/date'
import YumButton from '../YumButton/YumButton'
import { ROUTES } from '../../common/Routes/routePaths'
import YumSmallSelect from '../YumSmallSelect/YumSmallSelect'
import YumTabs from '../YumTabs/YumTabs'
import YumTab from '../YumTabs/YumTab/YumTab'
import { isPublicChallengeCompany, yuMuuvPublicCompanyId } from '../../views/Settings/Settings'
import './YumGoalList.scss'
import equal from 'fast-deep-equal'

import { isRtl } from '../../i18nInit'
import { YumGoalCard } from '../YumGoalCard/YumGoalCard'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { isNative } from '../../common/helpers/isNative'
import YumEmptyState from '../YumEmptyState/YumEmptyState'
import { ReactComponent as IconFind } from '../../assets/v3/icons/ranking/find.svg'

import { ReactComponent as EmptyStateIcon } from '../../assets/v3/icons/challenges-empty-state.svg'
import YumGrid from '../YumGrid/YumGrid'
import YumSearchField from '../YumSearchField/YumSearchField'
import { getUserCompanies } from '../../common/auth/authSelectors'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'

const ACTIVITY_TAB_PAST = 0
const ACTIVITY_TAB_CURRENT = 1
const ACTIVITY_TAB_FUTURE = 2

const ACTIVITY_TAB_TYPE_PRIVATE = 0
const ACTIVITY_TAB_TYPE_ORG = 1
const ACTIVITY_TAB_TYPE_PUBLIC = 2

export interface IYumGoalListProps {
  goals: GoalWithProgress[]
  loading: boolean
  activityListTab: number
  activityListTypeTab: number
  activityListTabSetAt: number
  hasAnyRole(roles: Role[]): boolean
  userCompanies: UserCompanyDto[]
}

export interface IYumGoalListDispatchProps {
  setActivityListTab(payload: number): void
  setActivityListTypeTab(payload: number): void
}

export class YumGoalList extends React.PureComponent<IYumGoalListProps & IYumGoalListDispatchProps> {
  appStateChangeListener: PluginListenerHandle | undefined
  state = {
    isSearchMode: false,
    searchValue: '',
  }

  preventEmptyCurrentView(goals: GoalWithProgress[], activityListTab: number) {
    //console.log('preventEmptyCurrentView | tab: ', activityListTab)
    // Current
    if (goals.length && activityListTab === ACTIVITY_TAB_CURRENT) {
      const currentGoals = goals.filter(
        (goal) =>
          convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
          convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
      )
      const currentOrgGoals = currentGoals.filter(
        (goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId,
      )
      const currentPrivateGoals = currentGoals.filter(
        (goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId,
      )
      const currentPublicGoals = currentGoals.filter((goal) => isPublicChallengeCompany(goal.companyId))
      // If public tab is active, and we have public goals, do nothing - all good.
      if (this.props.activityListTypeTab === ACTIVITY_TAB_TYPE_PUBLIC && currentPublicGoals.length) {
        return
      }
      // We have current org goals or current private goals.
      if (currentOrgGoals.length || currentPrivateGoals.length) {
        // We don't have current org goals.
        if (!currentOrgGoals.length) {
          // Set to private.
          this.setTypeTab(ACTIVITY_TAB_TYPE_PRIVATE)
        }
      }
      // We don't have current org goals or current private goals.
      else {
        const futureGoals = goals.filter(
          (goal) => convertUTCToLocalDate(new Date(goal.startDate)).getTime() > new Date().getTime(),
        )
        // We have future goals.
        if (futureGoals.length) {
          const orgGoals = futureGoals.filter((goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId)
          // We have future org goals.
          if (orgGoals.length) {
            // Set to org.
            this.setTypeTab(ACTIVITY_TAB_TYPE_ORG)
            // Set to future.
            this.setTab(ACTIVITY_TAB_FUTURE)
          }
          // We don't have future org goals.
          else {
            const privateGoals = futureGoals.filter(
              (goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId,
            )
            // We have future private goals.
            if (privateGoals.length) {
              // Set to private.
              this.setTypeTab(ACTIVITY_TAB_TYPE_PRIVATE)
              // Set to future.
              this.setTab(ACTIVITY_TAB_FUTURE)
            }
            // We don't have future private goals (nor org goals).
            else {
              const publicGoals = futureGoals.filter((goal) => isPublicChallengeCompany(goal.companyId))
              // We have future public goals.
              if (publicGoals.length) {
                // Set to public.
                this.setTypeTab(ACTIVITY_TAB_TYPE_PUBLIC)
                // Set to future.
                this.setTab(ACTIVITY_TAB_FUTURE)
              }
            }
          }
        }
        // No future goals.
        else {
          // Check if we have current public goals.
          if (currentGoals.length) {
            // Set to public.
            this.setTypeTab(ACTIVITY_TAB_TYPE_PUBLIC)
          }
        }
      }
    }
  }

  preventEmptyFutureView(goals: GoalWithProgress[]) {
    // Handle issue when challenge has started, but you're still on the future tab.
    const tabLastChanged = this.props.activityListTabSetAt
    if (goals.length && this.props.activityListTab === ACTIVITY_TAB_FUTURE && tabLastChanged > 0) {
      // TODO: If all challenges are still in the future, we should keep that view open.
      const timePassed = new Date().getTime() - tabLastChanged
      console.log('timePassed', timePassed)
      // 5 minutes has passed, set back to "current".
      if (timePassed > 300000) {
        // TODO: Why set to current, if there's nothing?
        /*// Set to current.
        console.log('Setting view to CURRENT')
        this.setTab(ACTIVITY_TAB_CURRENT)*/

        // We may need to change the tab type as well.
        const currentGoals = goals.filter(
          (goal) =>
            convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
            convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
        )
        const currentOrgGoals = currentGoals.filter(
          (goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId,
        )
        const currentPrivateGoals = currentGoals.filter(
          (goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId,
        )
        const currentPublicGoals = currentGoals.filter((goal) => isPublicChallengeCompany(goal.companyId))

        // If we have current ORG goals, set to ORG!
        // if (this.props.activityListTypeTab !== ACTIVITY_TAB_TYPE_ORG && currentOrgGoals.length) {
        if (currentOrgGoals.length) {
          // Set to current.
          console.log('Setting view to CURRENT and tab to ORG')
          this.setTab(ACTIVITY_TAB_CURRENT)
          this.setTypeTab(ACTIVITY_TAB_TYPE_ORG)
        } else {
          if (this.props.activityListTypeTab === ACTIVITY_TAB_TYPE_PRIVATE && currentPrivateGoals.length) {
            this.setTab(ACTIVITY_TAB_CURRENT)
            // this.setTypeTab(ACTIVITY_TAB_TYPE_PRIVATE)
          } else if (this.props.activityListTypeTab === ACTIVITY_TAB_TYPE_PUBLIC && currentPublicGoals.length) {
            this.setTab(ACTIVITY_TAB_CURRENT)
            // this.setTypeTab(ACTIVITY_TAB_TYPE_PUBLIC)
          }
        }

        return ACTIVITY_TAB_CURRENT
      }
    }
    return this.props.activityListTab
  }

  preventEmptyView(goals: GoalWithProgress[]) {
    console.log('preventEmptyView')
    const activityListTab = this.preventEmptyFutureView(goals)
    this.preventEmptyCurrentView(goals, activityListTab)
  }

  async componentDidMount() {
    console.log('YumGoalList | componentDidMount')
    this.preventEmptyView(this.props.goals)

    if (isNative()) {
      this.appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          console.log('YumGoalList | APP RESUME | preventEmptyFutureView')
          this.preventEmptyFutureView(this.props.goals)
        }
      })
    }

    // Add keyboard event listener for Escape key
    document.addEventListener('keydown', this.handleKeyDown)
  }

  componentWillUnmount() {
    if (isNative() && this.appStateChangeListener) {
      try {
        this.appStateChangeListener.remove()
      } catch (error) {
        console.error('YumGoalList | appStateChangeListener.remove() error', error)
      }
    }

    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  componentDidUpdate(
    prevProps: Readonly<IYumGoalListProps & IYumGoalListDispatchProps>,
    prevState: Readonly<{}>,
    snapshot?: any,
  ) {
    // if (!prevProps.goals.length && this.props.goals.length) {
    if (!equal(this.props.goals, prevProps.goals)) {
      console.log('YumGoalList | componentDidUpdate | goals props has changed -> preventEmptyView')
      this.preventEmptyView(this.props.goals)
    }
  }

  setTab = (index: number) => {
    this.props.setActivityListTab(index)
  }
  setTypeTab = (index: number) => {
    // @ts-ignore
    //if (index === this.props.activityListTypeTab) index = undefined
    this.props.setActivityListTypeTab(index)
  }

  handleTabChange = (event: React.ChangeEvent<{}>, value: number) => this.setTab(value)
  handleTypeTabChange = (event: React.ChangeEvent<{}>, value: number) => this.setTypeTab(value)

  handleTabIndexChange = (index: number) => this.setTab(index)

  handlePeriodChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: string }
    this.handleTabIndexChange(parseInt(value))
  }

  handleSearchIconClick = () => {
    this.setState({ isSearchMode: true })
  }

  handleSearchValueChange = (value: string) => {
    this.setState({ searchValue: value })
  }

  handleExitSearch = () => {
    if (!this.state.searchValue) {
      this.setState({ isSearchMode: false })
    } else {
      this.setState({ searchValue: '' })
    }
  }

  // Handle keyboard events
  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' /*&& this.state.isSearchMode*/) {
      this.handleExitSearch()
    }
  }

  renderFilteredList = (
    goals: GoalWithProgress[],
    activeTab: number,
    activeTypeTab: number,
    publicChallengesEnabled?: boolean,
  ) => {
    let list = []

    // If in search mode and search value exists, don't filter by tab type
    if (/*this.state.isSearchMode &&*/ this.state.searchValue) {
      list = goals
    } else {
      // Normal tab filtering
      switch (activeTypeTab) {
        // Private
        case ACTIVITY_TAB_TYPE_PRIVATE:
          list = goals.filter((goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId)
          break
        // Organization
        case ACTIVITY_TAB_TYPE_ORG:
          list = goals.filter((goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId)
          break
        // Public
        case ACTIVITY_TAB_TYPE_PUBLIC:
          list = goals.filter((goal) => isPublicChallengeCompany(goal.companyId))
          break
        default:
          list = publicChallengesEnabled ? goals : goals.filter((goal) => !isPublicChallengeCompany(goal.companyId))
      }
    }

    let type = ''

    // If in search mode and search value exists, don't filter by time period
    if (/*this.state.isSearchMode &&*/ this.state.searchValue) {
      type = 'search'
    } else {
      switch (activeTab) {
        // Past
        case ACTIVITY_TAB_PAST:
          list = list.filter((goal) => convertUTCToLocalDate(new Date(goal.endDate)).getTime() < new Date().getTime())
          type = 'past'
          break
        // Current
        case ACTIVITY_TAB_CURRENT:
          list = list
            .filter(
              (goal) =>
                convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
                convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
            )
            .reverse()
          type = 'current'
          break
        // Future
        case ACTIVITY_TAB_FUTURE:
          list = list.filter((goal) => convertUTCToLocalDate(new Date(goal.startDate)).getTime() > new Date().getTime())
          if (hasFeatureFlag(this.props.userCompanies, 'sortFutureChallengesByStartDate')) {
            // When flag is set: sort by startDate first, then endDate, then createdAt (ascending - nearest first)
            list = [...list].sort((a, b) => {
              const startA = convertUTCToLocalDate(new Date(a.startDate)).getTime()
              const startB = convertUTCToLocalDate(new Date(b.startDate)).getTime()
              if (startA !== startB) return startA - startB
              const endA = convertUTCToLocalDate(new Date(a.endDate)).getTime()
              const endB = convertUTCToLocalDate(new Date(b.endDate)).getTime()
              if (endA !== endB) return endA - endB
              const createdA = (a as GoalWithProgress & { createdAt?: number }).createdAt ?? 0
              const createdB = (b as GoalWithProgress & { createdAt?: number }).createdAt ?? 0
              return createdA - createdB
            })
          } else {
            // When flag is not set: use default reverse order
            list = list.reverse()
          }
          type = 'future'
          break
        default:
          return '-'
      }
    }

    // Determine the appropriate message based on whether we're in search mode
    const noChallenges = /*this.state.isSearchMode &&*/ this.state.searchValue
      ? t`No matches found.` //t`No challenges match your search.`
      : type === 'current'
        ? t`No active challenges.`
        : t`No matches found.`

    return (
      <Grid className={`yum-goal-list yum-goal-list--${type}`} container spacing={2} alignItems="stretch">
        {list.map((goal, i) => (
          <Grid className={`yum-goal-list-item`} key={goal.id} xs={12} sm={6} md={4} item>
            <YumGoalCard goal={goal} />
          </Grid>
        ))}
        {list.length === 0 && (
          <div className="user-activity user-activity--is-empty">
            <YumEmptyState text={`${noChallenges}..`} Icon={EmptyStateIcon} extraPx={30} />
          </div>
        )}
      </Grid>
    )
  }

  render() {
    const { userCompanies } = this.props
    let { goals } = this.props
    if (!goals) goals = []
    /*const pastGoals = goals.filter(
      (goal) => convertUTCToLocalDate(new Date(goal.endDate)).getTime() < new Date().getTime(),
    )
    const currentGoals = goals
      .filter(
        (goal) =>
          convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
          convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
      )
      .reverse()
    const futureGoals = goals
      .filter((goal) => convertUTCToLocalDate(new Date(goal.startDate)).getTime() > new Date().getTime())
      .reverse()*/

    const userCompaniesFiltered = userCompanies.filter(({ companyId }) => companyId != yuMuuvPublicCompanyId)

    const publicChallengesAllowed = userCompanies?.some(({ publicGoalsEnabled }) => publicGoalsEnabled)
    const publicChallengesEnabled = userCompanies?.find(({ companyId }) => companyId === yuMuuvPublicCompanyId)?.active

    const challengeSearchFeature = hasFeatureFlag(userCompanies, 'challengeSearch')
    const showSearchBtn = challengeSearchFeature

    // const currentLabel = ngettext(msgid`Current`, msgid`Current`, currentGoals.length)
    const currentLabel = c('plural').t`Current`

    const isAdmin = this.props.hasAnyRole([Role.ADMIN])

    const userGoalsEnabled = userCompaniesFiltered.some((uc) => uc.userGoalsEnabled)
    const allowedToCreateChallenges = isAdmin || userGoalsEnabled

    const joinedPrivateGoal = userCompaniesFiltered.some((uc) => !!uc.ownerUserId)
    const joinedPublicGoal = userCompaniesFiltered.some((uc) => isPublicChallengeCompany(uc.companyId))
    const hasPrivateGoals = goals.some((goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId)

    const showPrivateTab = userGoalsEnabled || joinedPrivateGoal || hasPrivateGoals

    const activeTab = this.props.activityListTab === undefined ? ACTIVITY_TAB_CURRENT : this.props.activityListTab
    let activeTypeTab =
      this.props.activityListTypeTab === undefined ? ACTIVITY_TAB_TYPE_ORG : this.props.activityListTypeTab

    // Only show public for PAST if someone participated, as it's deprecated
    const showPublicTab =
      activeTab === ACTIVITY_TAB_PAST && (publicChallengesAllowed || joinedPublicGoal) && publicChallengesEnabled

    if (
      (activeTypeTab === ACTIVITY_TAB_TYPE_PRIVATE && !showPrivateTab) ||
      (activeTypeTab === ACTIVITY_TAB_TYPE_PUBLIC && !showPublicTab)
    )
      activeTypeTab = ACTIVITY_TAB_TYPE_ORG

    // Filter goals based on search value if in search mode
    let filteredGoals = goals
    if (/*this.state.isSearchMode &&*/ this.state.searchValue) {
      const searchTerm = this.state.searchValue.toLowerCase()
      filteredGoals = goals.filter(
        (goal) => goal.title?.toLowerCase().includes(searchTerm),
        //|| (goal.description && goal.description.toLowerCase().includes(searchTerm))
      )
    }

    const showTabs = showPrivateTab || showPublicTab
    const searchFieldIsAlwaysVisible = !showTabs && challengeSearchFeature
    const showSearchField = this.state.isSearchMode || searchFieldIsAlwaysVisible

    return (
      <Card>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          style={{ marginBottom: '1rem' }}
          wrap="nowrap"
        >
          <Grid item style={{ overflow: 'hidden' }}>
            <h1
              style={{ marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >{t`Challenges`}</h1>
          </Grid>

          <Grid item style={{ whiteSpace: 'nowrap' }}>
            {/*{!this.state.isSearchMode || !this.state.searchValue ? (*/}
            {!this.state.searchValue ? (
              <YumSmallSelect variant="outlined" value={activeTab} onChange={this.handlePeriodChange}>
                <option value={0}>{t`Past`}</option>
                <option value={1}>{currentLabel}</option>
                <option value={2}>{t`Future`}</option>
              </YumSmallSelect>
            ) : null}
            {allowedToCreateChallenges && (
              <YumButton
                size="small"
                color="secondary"
                to={ROUTES.CHALLENGE_ADMIN_CREATE.to}
                style={{
                  marginLeft: isRtl ? 0 : '10px',
                  marginRight: isRtl ? '10px' : 0,
                }}
              >
                {t`Create`}
              </YumButton>
            )}
          </Grid>
        </Grid>

        {this.props.loading ? (
          <Loader />
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '16px',
                marginLeft: 'auto',
                marginRight: 'auto',
                maxWidth: { md: '440px' },
              }}
            >
              {showSearchField ? (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                  <YumSearchField
                    value={this.state.searchValue}
                    onChange={this.handleSearchValueChange}
                    placeholder={t`Search`}
                    autoFocus={!searchFieldIsAlwaysVisible}
                    style={{ width: '100%' }}
                    onClear={this.handleExitSearch}
                    alwaysShowClearButton={!searchFieldIsAlwaysVisible}
                  />
                </Box>
              ) : (
                <>
                  {showTabs ? (
                    <YumTabs
                      className={'yum-goal-list__tabs'}
                      value={activeTypeTab}
                      onChange={this.handleTypeTabChange}
                    >
                      {showPrivateTab && <YumTab label={t`Private`} value={0} />}
                      <YumTab label={t`Organizational`} value={1} />
                      {showPublicTab && <YumTab label={t`Public`} value={2} />}
                    </YumTabs>
                  ) : null}
                  {showSearchBtn ? (
                    <YumButton
                      variant="outlined"
                      size="small"
                      style={
                        isRtl
                          ? { marginRight: '8px', width: '36px', height: '36px' }
                          : { marginLeft: '8px', width: '36px', height: '36px' }
                      }
                      onClick={this.handleSearchIconClick}
                    >
                      <IconFind />
                    </YumButton>
                  ) : null}
                </>
              )}
            </Box>

            {this.renderFilteredList(
              this.state.isSearchMode || (searchFieldIsAlwaysVisible && this.state.searchValue) ? filteredGoals : goals,
              activeTab,
              activeTypeTab,
              showPublicTab,
            )}
          </>
        )}
      </Card>
    )
  }
}
